import React from 'react';
import { Box, Button, Stack, Typography, Paper, Chip } from '@mui/material';
import { Inventory2Outlined, SupportAgentOutlined, ArrowForwardOutlined } from '@mui/icons-material';
import AuthShell from '../components/AuthShell';
import { useAuth } from '../providers/AuthProvider';

function getDefaultItamBase(apps = [], appKey = 'itam') {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
      if (appKey === 'helpdesk') {
        return (process.env.REACT_APP_HELPDESK_URL || 'http://localhost:3002').replace(/\/+$/, '');
      }
      return (process.env.REACT_APP_ITAM_URL || 'http://localhost:3001').replace(/\/+$/, '');
    }
  }
  const appList = Array.isArray(apps) ? apps : [];
  const appUrl = appList.find((app) => app?.key === appKey)?.url;
  if (appUrl) return appUrl;
  const envBase = process.env.REACT_APP_ITAM_URL ? process.env.REACT_APP_ITAM_URL.replace(/\/+$/, '') : '';
  if (appKey === 'helpdesk') {
    const helpdeskBase = process.env.REACT_APP_HELPDESK_URL ? process.env.REACT_APP_HELPDESK_URL.replace(/\/+$/, '') : '';
    if (helpdeskBase) return helpdeskBase;
    return 'https://helpdesk.sushaservices.com';
  }
  if (envBase) return envBase;
  return 'https://itam.sushaservices.com';
}

function AppTile({ title, subtitle, onOpen, icon, note }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2.5, md: 3 },
        borderColor: '#dbe7f5',
        height: '100%',
        width: '100%',
        flex: 1,
        borderRadius: 3,
        boxShadow: '0 18px 40px rgba(15, 23, 42, 0.04)',
        background:
          'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,251,255,1) 100%)',
      }}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: '#eff6ff',
            color: 'primary.main',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {subtitle}
          </Typography>
        </Box>
        {note ? (
          <Chip
            label={note}
            size="small"
            variant="outlined"
            sx={{
              alignSelf: 'flex-start',
              fontWeight: 700,
              borderColor: '#cbd5e1',
              bgcolor: '#f8fafc',
            }}
          />
        ) : null}
        <Button
          onClick={onOpen}
          variant="contained"
          endIcon={<ArrowForwardOutlined />}
          sx={{ alignSelf: 'flex-start', px: 2.3, py: 1.1, textTransform: 'none', fontWeight: 700 }}
        >
          Continue
        </Button>
      </Stack>
    </Paper>
  );
}

function formatRoleLabel(role) {
  return String(role || 'Member')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export default function ChooseAppPage() {
  const auth = useAuth();
  const apps = Array.isArray(auth.apps) ? auth.apps : [];
  const itamUrl = getDefaultItamBase(apps, 'itam');
  const helpdeskUrl = getDefaultItamBase(apps, 'helpdesk');
  const assetCount = Number(auth.enterprise?.asset_count || 0);
  const roleLabel = formatRoleLabel(auth.role || auth.user?.role);
  const roleContext =
    auth.role === 'super' || auth.role === 'superadmin'
      ? 'Super Admin access'
      : auth.role === 'partner'
        ? 'Partner access'
        : 'Enterprise access';

  const buildHandoffUrl = (baseUrl, appKey, returnTo = '/enterprise') => {
    const sessionPayload = {
      token: auth.token || null,
      selected_app: appKey,
      user: auth.user || null,
      enterprise: auth.enterprise || null,
      role: auth.role || null,
      memberships: Array.isArray(auth.memberships) ? auth.memberships : [],
      page_access: Array.isArray(auth.user?.page_access) ? auth.user.page_access : [],
      asset_count: assetCount,
      apps,
    };
    const params = new URLSearchParams({
      token: auth.token || '',
      role: auth.role || '',
      enterprise_id: auth.enterprise?.id ? String(auth.enterprise.id) : '',
      app: appKey,
      returnTo,
      session: JSON.stringify(sessionPayload),
    });
    return `${baseUrl.replace(/\/+$/, '')}/oauth/callback#${params.toString()}`;
  };
  const openApp = (baseUrl, appKey) => {
    auth.setSession({
      token: auth.token,
      user: auth.user,
      enterprise: auth.enterprise,
      role: auth.role,
      memberships: Array.isArray(auth.memberships) ? auth.memberships : [],
      apps,
      selected_app: appKey,
    });
    console.log('[auth] workspace handoff', {
      baseUrl,
      appKey,
      returnTo: '/enterprise',
      sessionPayload: {
        token: auth.token || null,
        selected_app: appKey,
        user: auth.user || null,
        enterprise: auth.enterprise || null,
        role: auth.role || null,
        memberships: Array.isArray(auth.memberships) ? auth.memberships : [],
        page_access: Array.isArray(auth.user?.page_access) ? auth.user.page_access : [],
        asset_count: assetCount,
        apps,
      },
    });
    window.location.assign(buildHandoffUrl(baseUrl, appKey, '/enterprise'));
  };

  return (
    <AuthShell
      showWorkspaceSwitchers={false}
      topBrandImageSrc="/site.png"
    >
      <Stack spacing={3.5}>
        <Box sx={{ maxWidth: 760, mx: 'auto', width: '100%' }}>
          <Chip
            label={roleContext}
            size="small"
            sx={{
              mb: 1.5,
              bgcolor: '#dbeafe',
              color: '#0b4ea2',
              fontWeight: 800,
            }}
          />
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.7rem', md: '2.15rem' },
              fontWeight: 800,
              color: '#0f172a',
              mb: 1,
            }}
          >
            Welcome
          </Typography>
          <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.7 }}>
            Choose the workspace you want to access.
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1,
              color: '#64748b',
              fontWeight: 600,
            }}
          >
            Signed in as {roleLabel}
          </Typography>
        </Box>

        <Stack spacing={2.5} direction={{ xs: 'column', md: 'row' }} sx={{ alignItems: 'stretch' }}>
          <AppTile
            title="IT Asset Management"
            subtitle="Open the operational workspace for assets, devices, maintenance, and reporting."
            onOpen={() => openApp(itamUrl, 'itam')}
            icon={<Inventory2Outlined fontSize="medium" />}
          />
          <AppTile
            title="Helpdesk"
            subtitle="Open the service workspace for tickets and customer support operations."
            onOpen={() => openApp(helpdeskUrl, 'helpdesk')}
            icon={<SupportAgentOutlined fontSize="medium" />}
          />
        </Stack>
      </Stack>
    </AuthShell>
  );
}
